import { createSignal, onCleanup, onMount, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Button } from '../../ui-components/button/button';
import { Link } from '../../ui-components/link/link';
import { StyledErrorMessage, StyledLoginForm, StyledLoginFormContent, StyledLoginInput, StyledSuccessMessage } from './login-form-styles';
import { useLocation, useNavigate } from '@solidjs/router';
import { logIn } from '../../tools/login-user';
import { StyledFlexRow } from '../../ui-components/utility-style-components/flex';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { handleResendVerification } from '../../tools/verify-user';

type LoginFormProps = { 
    firstElement: (el: any) => void
    lastElement: (el: any) => void
};

export const LoginForm = (props: LoginFormProps) => {

    let emailInputRef: HTMLInputElement | undefined;
    const { 
        appStore,
        pageRegistration,
        localize,
        pageRequestPasswordResetUrl,
        updateUser,
        recaptchaSiteKey,
        deploymentEnvironment,
        ssoBaseUrl,
        graphQlClient,
    } = useContext(AppContext);
    const { closeLoginMenu } = appStore;
    const [emailValue, setEmailValue] = createSignal('');
    const [passwordValue, setPasswordValue] = createSignal('');
    const [errorMessage, setErrorMessage] = createSignal('');
    const [successMessage, setSuccessMessage] = createSignal('');
    const [showResendVerification, setShowResendVerification] = createSignal(false);
    const [resendingVerification, setResendingVerification] = createSignal(false);
    const [verificationResent, setVerificationResent] = createSignal(false);
    const [signingIn, setSigningIn] = createSignal(false);
    const navigate = useNavigate();
    const location = useLocation();

    onMount(() => {
        const handleKeyPress = (e: any) => {
            if (e.keyCode === 13 && emailValue() !== '' && passwordValue() !== '') {
                handleLogin();
            }
        };

        document.addEventListener('keypress', handleKeyPress);

        if (!document.querySelector('#grecaptcha')) {
            const scriptTag = document.createElement('script');
            scriptTag.type = 'text/javascript';
            scriptTag.id = 'grecaptcha';
            scriptTag.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
            document.head.appendChild(scriptTag);
        }

        if (emailInputRef) {
            emailInputRef.focus();
        }
        onCleanup(() => document.removeEventListener('keypress', handleKeyPress));
    });

    const handleLogin = async () => {
        const args = {
            email: emailValue(),
            password: passwordValue(),
        };

        setSigningIn(true);
        const { success, message } = await logIn(args, updateUser, recaptchaSiteKey, deploymentEnvironment, ssoBaseUrl);

        setSigningIn(false);
        setShowResendVerification(false);
        setVerificationResent(false);

        if (!success) {
            if (message === 'user-not-found') {
                setErrorMessage(localize('login-user-not-found', 'No user exists with this email'));
            } else if (message === 'invalid-credentials') {
                setErrorMessage(localize('invalid-credentials', 'Invalid credentials'));
            } else if (message === 'user-not-verified') {
                setErrorMessage(localize('user-not-verified', 'Your account is not verified yet. Please click the button below and then follow the verification link sent to your email to activate your account.'));
                setShowResendVerification(true);
            } else {
                setErrorMessage(localize('error-has-occurred', 'An error has occurred, please try again later'));
            }

            return;
        }

        closeLoginMenu();
    };

    const handleResendVerificationOnclick = async () => {
        setResendingVerification(true);

        try {
            const success =  await handleResendVerification(emailValue(), graphQlClient, location.pathname);
            if (success) {
                setSuccessMessage(localize('verification-email-sent', 'Verification email sent! Please check your inbox.'));
            } else {
                throw new Error();
            }
        } catch (e) {
            setErrorMessage(localize('error-has-occurred', 'An error has occurred, please try again later'));
        } finally {
            setErrorMessage('');
            setShowResendVerification(false);
            setVerificationResent(true);
            setResendingVerification(false);
        }
    };

    return (
        <StyledLoginForm>
            <StyledLoginFormContent>
                <StyledLoginInput
                    ref={(el: HTMLElement) => {
                        emailInputRef = el as HTMLInputElement;
                        props.firstElement(el);
                    }}
                    value={emailValue()}
                    onInput={(e) => setEmailValue(e.currentTarget.value)}
                    placeholder={localize('email' ,'Email')}
                    type="email"
                />
                <StyledLoginInput
                    value={passwordValue()}
                    onInput={(e) => setPasswordValue(e.currentTarget.value)}
                    placeholder={localize('password' ,'Password')} type="password"
                />
                <Show when={errorMessage()} fallback={<div style={{height: '22px'}}></div>}>
                    <StyledErrorMessage>
                        {errorMessage()}
                    </StyledErrorMessage>
                </Show>

                <Show when={successMessage()}>
                    <StyledSuccessMessage>
                        {successMessage()}
                    </StyledSuccessMessage>
                </Show>

                <Show when={showResendVerification()}>
                    <StyledFlexRow gap='1rem'>
                        <Button
                            label={localize('resend-verification-email', 'Resend verification email')}
                            onClick={handleResendVerificationOnclick}
                            disabled={resendingVerification()}
                        />
                        <Show when={resendingVerification()}>
                            <LoadingSpinner />
                        </Show>
                    </StyledFlexRow>
                </Show>
                
                <Show when={!showResendVerification() && !verificationResent()}>
                    <StyledFlexRow gap='1.5rem'>
                        <Button
                            label={localize('sign-in' ,'Sign in')} 
                            onClick={() => handleLogin()}
                            disabled={signingIn() || showResendVerification() || verificationResent() || emailValue() === '' || passwordValue() === ''}
                        />
                        <Show when={signingIn()}>
                            <LoadingSpinner />
                        </Show>
                    </StyledFlexRow>

                    <Link
                        label={localize('reset-password' ,'Reset password')}
                        url={pageRequestPasswordResetUrl}
                        onClick={() => closeLoginMenu()}
                    />

                    <Link
                        asButton={true}
                        label={localize('register-a-new-user-account' ,'Register a new user account')}
                        element={(el: HTMLElement) => props.lastElement(el)}
                        url={'/'}
                        onClick={() => {
                            navigate(pageRegistration.url, { state: { fromUrl: location.pathname }} );
                            closeLoginMenu();
                        }}
                    />
                </Show>
            </StyledLoginFormContent>
        </StyledLoginForm>
    );
};

